// FechamentoCabecalho.tsx
// Fechamento de Ciclo: cabeçalho "trilha" (opção A do desenho aprovado pelo Walber no Claude Design em
// 07/10/2026). SÓ TELA: não chama o motor, não grava nada e não faz conta de índice. Recebe do
// FechamentoCiclo.tsx o que já está na tela e só muda a forma de apresentar.
//  - FechamentoCabecalho: ciclo + resumo das áreas + as 6 abas em 4 grupos, todas visíveis (sem rolar).
//  - BarraDaAba: o cartão "onde estou" de cada aba (nome, para que serve, botões e, na aba 2, as áreas).
//  - AreasBotoes: as áreas como botões, cada uma com o sinal da situação (no lugar da caixa de seleção).
//  - ehSemRG / estadoDaArea / textoRascunho / totalComDatas: pequenas ajudas de texto, sem conta.
// Ordem das abas (07/10/2026): 0 Painel · 1 Quadro por Área · 2 Condensado (Amargosa) ·
// 3 Condensado TDAH-friendly · 4 Consolidado Digitação · 5 Instruções.
// Tudo aqui leva a classe fc-noprint: nada deste arquivo sai na impressão das folhas.
import React from 'react';
import { Box, Card, Typography, Select, MenuItem, CircularProgress, ButtonBase } from '@mui/material';
import { CheckCircleOutlined, WarningAmber, PriorityHigh, Block, Autorenew } from '@mui/icons-material';

type V = number | string | null;

export type EstadoArea = 'pronta' | 'falta_rg' | 'andamento' | 'precisa' | 'bloqueada';

export interface AreaResumo {
  id: number;
  nome: string;
  estado: EstadoArea;
  rotulo: string;
  nota?: string;
}

interface CicloOpcao { id: number; nome: string; ativo?: boolean; }

// Área sem Reconhecimento Geográfico cadastrado: os imóveis existentes não vêm do RG
export function ehSemRG(l: { imov_exist: { fonte: string } }): boolean {
  return l.imov_exist.fonte !== 'RG';
}

export function estadoDaArea(l: { situacao: string; imov_exist: { fonte: string } }): EstadoArea {
  if (l.situacao === 'pronta') return 'pronta';
  if (l.situacao === 'bloqueada') return 'bloqueada';
  if (l.situacao === 'precisa_de_voce') return 'precisa';
  return ehSemRG(l) ? 'falta_rg' : 'andamento';
}

// Faixa RASCUNHO do Condensado e do Consolidado: separa "falta cadastrar o RG" de "em andamento"
export function textoRascunho(emAndamento: string[], semRG: string[]): string {
  const faltam = emAndamento.filter((a) => semRG.indexOf(a) >= 0);
  const outras = emAndamento.filter((a) => semRG.indexOf(a) < 0);
  const partes: string[] = [];
  if (faltam.length) partes.push(`falta cadastrar o RG: ${faltam.join(', ')}`);
  if (outras.length) partes.push(`área(s) em andamento: ${outras.join(', ')}`);
  return `RASCUNHO — ${partes.join(' · ')}`;
}

// Linha TOTAL com as datas do ciclo (1º início e último término), que o motor já manda na linha do município
export function totalComDatas(total: Record<string, V>, consolidado?: Record<string, V>): Record<string, V> {
  return {
    ...total,
    inic: total.inic ?? consolidado?.inic ?? null,
    term: total.term ?? consolidado?.term ?? null,
  };
}

interface AbaInfo { n: number; nome: string; sub: string; texto: string; }

export const ABAS_FECHAMENTO: AbaInfo[] = [
  { n: 1, nome: 'Painel do Ciclo', sub: 'O que falta resolver', texto: 'As áreas do ciclo e o que falta em cada uma.' },
  { n: 2, nome: 'Quadro por Área', sub: 'A folha de cada área', texto: 'A folha de uma área por vez.' },
  { n: 3, nome: 'Condensado (Amargosa)', sub: 'A folha que vai para a Regional', texto: 'Uma linha por área + TOTAL. É a folha que você passa para o papel.' },
  { n: 4, nome: 'Condensado TDAH-friendly', sub: 'A mesma folha, passo a passo', texto: 'O mesmo Condensado, um bloco de cada vez.' },
  { n: 5, nome: 'Consolidado Digitação', sub: 'O município em uma linha', texto: 'O município inteiro em uma linha. É o que o Paulinho digita.' },
  { n: 6, nome: 'Instruções', sub: 'Como usar esta tela', texto: 'O passo a passo do fechamento e as regras das contas.' },
];

const GRUPOS: { rot: string; flex: string; abas: number[] }[] = [
  { rot: 'Conferir', flex: '2 1 340px', abas: [0, 1] },
  { rot: 'Passar para o papel', flex: '2 1 340px', abas: [2, 3] },
  { rot: 'Entregar para digitar', flex: '1 1 170px', abas: [4] },
  { rot: 'Ajuda', flex: '1 1 170px', abas: [5] },
];

const ESTADOS: Record<EstadoArea, { cor: 'success' | 'warning' | 'info' | 'error'; resumo: (n: number) => string }> = {
  pronta: { cor: 'success', resumo: (n) => `${n} ${n === 1 ? 'pronta' : 'prontas'}` },
  falta_rg: { cor: 'warning', resumo: (n) => `${n} ${n === 1 ? 'falta' : 'faltam'} cadastrar o RG` },
  andamento: { cor: 'info', resumo: (n) => `${n} em andamento` },
  precisa: { cor: 'warning', resumo: (n) => `${n} ${n === 1 ? 'precisa' : 'precisam'} de você` },
  bloqueada: { cor: 'error', resumo: (n) => `${n} ${n === 1 ? 'bloqueada' : 'bloqueadas'}` },
};
const ORDEM_RESUMO: EstadoArea[] = ['bloqueada', 'precisa', 'falta_rg', 'andamento'];

const ROTULO_GRUPO = { fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' } as const;

function Sinal({ estado, cor }: { estado: EstadoArea; cor?: string }) {
  const sx = { fontSize: 20, color: cor ?? `${ESTADOS[estado].cor}.dark` };
  if (estado === 'pronta') return <CheckCircleOutlined sx={sx} />;
  if (estado === 'falta_rg') return <WarningAmber sx={sx} />;
  if (estado === 'precisa') return <PriorityHigh sx={sx} />;
  if (estado === 'bloqueada') return <Block sx={sx} />;
  return <Autorenew sx={sx} />;
}

// ─── Cabeçalho: ciclo + resumo das áreas + trilha das abas ──────────────────────────────────────
export default function FechamentoCabecalho({ ciclos, cicloId, onCiclo, carregando, aba, onAba, areas }: {
  ciclos: CicloOpcao[];
  cicloId: number | '';
  onCiclo: (id: number) => void;
  carregando: boolean;
  aba: number;
  onAba: (indice: number) => void;
  areas: AreaResumo[];
}) {
  const prontas = areas.filter((a) => a.estado === 'pronta').length;
  const cicloSel = ciclos.find((c) => c.id === cicloId);
  return (
    <Card className="fc-noprint" sx={{ mb: 1.5, borderRadius: 3, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px 20px' }}>
        <Select
          size="small"
          value={cicloId}
          onChange={(e) => onCiclo(Number(e.target.value))}
          displayEmpty
          inputProps={{ 'aria-label': 'Ciclo' }}
          renderValue={() => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography component="span" sx={{ fontSize: 20, lineHeight: '28px', fontWeight: 900 }}>
                {cicloSel ? cicloSel.nome : 'Escolha o ciclo'}
              </Typography>
              {cicloSel?.ativo && (
                <Box component="span" sx={{ px: 1.25, py: 0.25, borderRadius: 99, bgcolor: 'success.main', color: 'success.contrastText', fontSize: 13, fontWeight: 700 }}>
                  Ativo
                </Box>
              )}
            </Box>
          )}
          sx={{ minWidth: 220, bgcolor: 'background.paper', borderRadius: 2.5, '& .MuiSelect-select': { py: 1 } }}
        >
          {ciclos.map((c) => <MenuItem key={c.id} value={c.id}>{c.nome}{c.ativo ? ' (ativo)' : ''}</MenuItem>)}
        </Select>
        {carregando && <CircularProgress size={22} />}
        <Box sx={{ flex: '1 1 40px' }} />
        {areas.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '4px 12px', fontSize: 14, fontWeight: 700 }}>
              <Box component="span" sx={{ color: 'success.dark' }}>{prontas} de {areas.length} áreas prontas</Box>
              {ORDEM_RESUMO.map((e) => {
                const n = areas.filter((a) => a.estado === e).length;
                return n ? <Box key={e} component="span" sx={{ color: `${ESTADOS[e].cor}.dark` }}>{ESTADOS[e].resumo(n)}</Box> : null;
              })}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {areas.map((a) => (
                <Box
                  key={a.id}
                  title={`${a.nome}: ${a.rotulo}`}
                  sx={{
                    width: 34, height: 10, borderRadius: 5, border: '1.5px solid',
                    borderColor: `${ESTADOS[a.estado].cor}.dark`,
                    bgcolor: a.estado === 'pronta' ? 'success.main' : 'transparent',
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{ height: '1px', bgcolor: 'divider' }} />

      <Box component="nav" aria-label="Abas do Fechamento de Ciclo" sx={{ display: 'flex', flexWrap: 'wrap', gap: '14px 20px' }}>
        {GRUPOS.map((g) => (
          <Box key={g.rot} sx={{ flex: g.flex, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography sx={ROTULO_GRUPO}>{g.rot}</Typography>
            <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
              {g.abas.map((i) => {
                const t = ABAS_FECHAMENTO[i];
                const ativa = i === aba;
                return (
                  <ButtonBase
                    key={i}
                    onClick={() => onAba(i)}
                    aria-current={ativa ? 'page' : undefined}
                    sx={{
                      flex: '1 1 0', minWidth: 0, minHeight: 72, p: '10px 12px', gap: 1.25,
                      display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', textAlign: 'left',
                      border: '1.5px solid', borderRadius: 3,
                      borderColor: ativa ? 'primary.main' : 'divider',
                      bgcolor: ativa ? 'primary.main' : 'background.paper',
                      color: ativa ? 'primary.contrastText' : 'text.primary',
                      '&:hover': { borderColor: 'primary.main' },
                      '&.Mui-focusVisible': { outline: '3px solid', outlineColor: 'primary.light', outlineOffset: 2 },
                    }}
                  >
                    <Box component="span" sx={{
                      flexShrink: 0, width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 15, fontWeight: 900,
                      bgcolor: ativa ? 'background.paper' : 'action.selected',
                      color: ativa ? 'primary.main' : 'text.primary',
                    }}>{t.n}</Box>
                    <Box component="span" sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' }}>
                      <Box component="span" sx={{ fontSize: 15, lineHeight: '19px', fontWeight: 700 }}>{t.nome}</Box>
                      <Box component="span" sx={{ fontSize: 13, lineHeight: '16px', color: ativa ? 'inherit' : 'text.secondary', opacity: ativa ? 0.92 : 1 }}>{t.sub}</Box>
                    </Box>
                  </ButtonBase>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    </Card>
  );
}

// ─── Cartão "onde estou": nome da aba, para que serve, botões e (na aba 2) as áreas ─────────────
export function BarraDaAba({ aba, children, extra }: { aba: number; children?: React.ReactNode; extra?: React.ReactNode }) {
  const t = ABAS_FECHAMENTO[aba];
  if (!t) return null;
  return (
    <Card className="fc-noprint" sx={{ mb: 2, borderRadius: 3, p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px 16px' }}>
        <Box sx={{ flex: '999 1 320px', minWidth: 0, display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '4px 12px' }}>
          <Typography component="h2" sx={{ fontSize: 18, lineHeight: '24px', fontWeight: 900 }}>{t.n} · {t.nome}</Typography>
          <Typography sx={{ fontSize: 14, lineHeight: '20px', color: 'text.secondary' }}>{t.texto}</Typography>
        </Box>
        {children && <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25 }}>{children}</Box>}
      </Box>
      {extra}
    </Card>
  );
}

// ─── As áreas como botões (aba 2), com o sinal da situação e a situação da área escolhida ───────
export function AreasBotoes({ areas, valor, onEscolher }: { areas: AreaResumo[]; valor: number | ''; onEscolher: (id: number) => void }) {
  const sel = areas.find((a) => a.id === valor);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <Typography component="span" sx={{ ...ROTULO_GRUPO, mr: 0.5 }}>Área</Typography>
        {areas.map((a) => {
          const ativa = a.id === valor;
          return (
            <ButtonBase
              key={a.id}
              onClick={() => onEscolher(a.id)}
              aria-pressed={ativa}
              title={a.rotulo}
              sx={{
                height: 44, pl: 1.25, pr: 1.75, gap: 0.75, borderRadius: 99, border: '1.5px solid',
                fontSize: 15, fontWeight: ativa ? 700 : 500,
                borderColor: ativa ? 'primary.main' : 'divider',
                bgcolor: ativa ? 'primary.main' : 'background.paper',
                color: ativa ? 'primary.contrastText' : 'text.primary',
                '&:hover': { borderColor: 'primary.main' },
                '&.Mui-focusVisible': { outline: '3px solid', outlineColor: 'primary.light', outlineOffset: 2 },
              }}
            >
              <Sinal estado={a.estado} cor={ativa ? 'inherit' : undefined} />
              {a.nome}
            </ButtonBase>
          );
        })}
      </Box>
      {sel && (
        <Box sx={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 14px', px: 1.75, py: 1.25, borderRadius: 2.5,
          border: '1px solid', borderColor: `${ESTADOS[sel.estado].cor}.main`,
        }}>
          <Sinal estado={sel.estado} />
          <Typography component="span" sx={{ fontSize: 15, fontWeight: 700, color: `${ESTADOS[sel.estado].cor}.dark` }}>{sel.nome} · {sel.rotulo}</Typography>
          {sel.nota && <Typography component="span" sx={{ fontSize: 14, color: 'text.secondary' }}>{sel.nota}</Typography>}
        </Box>
      )}
    </Box>
  );
}
